'use client';

import {
  Button,
  Card,
  EButtonColorVariant,
  EButtonSizeVariant,
  EHeadingStyleVariant,
  ETextStyleVariant,
  GeneralCalendarIcon,
  GeneralCopyIcon,
  GeneralLinkIcon,
  GeneralMailIcon,
  GeneralMessageMessageCircleIcon,
  GeneralPhonePhoneCallIcon,
  GeneralUserUsersIcon,
  Heading,
  Text,
  TextInput,
} from '@outdoorsyco/bonfire';
import cn from 'classnames';
import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';

import DocumentsNeeded from '@/pages/claims/status/[claimNumber]/DocumentsNeeded';
import { trackEvent } from '@/services/track-event';

type ClaimStatus = 'draft' | 'under_review' | 'pending' | 'open' | 'closed' | 'cancelled';

type TimelineStep = {
  label: string;
  description?: string;
  descriptionIsHtml?: boolean;
  date?: string;
  status: 'completed' | 'in_progress' | 'pending';
};

type ClaimsStatusTrackerProps = {
  claim_number: string;
  status: ClaimStatus;
  coverage_type: string;
  vehicle_description: string;
  incident_date: string;
  incident_location: string;
  filed_date: string;
  closed_at?: string;
  estimated_resolution: string;
  adjuster_name: string;
  claim_team_email: string;
  policy_number: string;
  current_step: number;
  total_steps: number;
  current_step_label: string;
  last_updated: string;
  timeline_steps: Array<TimelineStep>;
  next_update_date?: string;
  next_update_description?: string;
  document_needed?: boolean;
  onNotifyEmail?: () => void;
  onNotifyText?: () => void;
  onBack?: () => void;
  reload?: () => void;
  isLoading?: boolean;
};

type Faq = {
  title: string;
  content?: string;
  link?: string;
};

const faqs: Array<Faq> = [
  {
    title: 'Host Comprehensive and Collision Policy',
    link: 'https://support.outdoorsy.com/hc/en-us/articles/37427542273435-Comprehensive-and-collision-coverage-for-hosts-US-rentals',
  },
  {
    title: 'Guest Comprehensive and Collision Policy',
    link: 'https://support.outdoorsy.com/hc/en-us/articles/37427399948315-Comprehensive-and-collision-coverage-for-guests-US-rentals',
  },
  {
    title: 'Outdoorsy Protection Packages',
    link: 'https://support.outdoorsy.com/hc/en-us/articles/37646881475355-RV-Protection-Packages',
  },
  {
    title: 'Outdoorsy Help Center',
    link: 'https://support.outdoorsy.com/hc/en-us',
  },
  {
    title: 'Wheelbase Help Center',
    link: 'https://help.wheelbasepro.com/hc/en-us',
  },
  {
    title: 'Terms of Service and Legal',
    link: 'https://support.outdoorsy.com/hc/en-us/categories/37278373970459-Terms-of-Service-Legal',
  },
];

const STATUS_CONFIG: Record<ClaimStatus, { label: string; className: string }> = {
  draft: {
    label: 'Draft',
    className: 'bg-blue-100 text-blue-800',
  },
  under_review: {
    label: 'Under Review',
    className: 'bg-blue-100 text-blue-800',
  },
  pending: {
    label: 'Pending',
    className: 'bg-canvas-300 text-canvas-300',
  },
  open: {
    label: 'Open',
    className: 'bg-canvas-200',
  },
  closed: {
    label: 'Closed',
    className: 'bg-gray-50 text-canvas-300',
  },
  cancelled: {
    label: 'Cancelled',
    className: 'bg-red-50 text-red-500',
  },
};

function StatusBadge({ status, label }: { status: ClaimStatus; label: string }) {
  const config = STATUS_CONFIG[status];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium',
        config.className,
      )}>
      <span className="w-2 h-2 rounded-full bg-canvas-300" />
      {label || config.label}
    </span>
  );
}

function StepIcon({ status, num }: { status: TimelineStep['status']; num: number }) {
  if (status === 'completed') {
    return (
      <div className="flex items-center justify-center w-8 h-8 text-white bg-green-500 rounded-full shrink-0">
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>
    );
  }
  if (status === 'in_progress') {
    return (
      <svg
        className="w-8 h-8 shrink-0"
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg">
        <circle cx="16" cy="16" r="15" fill="#f2e5c9" />
        <circle cx="16" cy="16" r="11" fill="#FFFFFF" />
        <circle cx="16" cy="16" r="10" fill="#e5b01f" />
        <circle cx="16" cy="16" r="2.5" fill="#FFFFFF" />
      </svg>
    );
  }
  return (
    <div className="flex items-center justify-center w-8 h-8 text-gray-300 border-2 border-dashed rounded-full shrink-0 border-gray-250 bg-gray-10">
      {num + 1}
    </div>
  );
}

function ClaimsTimeline({ steps }: { steps: Array<TimelineStep> }) {
  return (
    <div className="p-4 md:p-8">
      {steps?.map((step, i) => (
        <div key={step.label} className="flex gap-4">
          <div className="flex flex-col items-center shrink-0">
            <StepIcon status={step.status} num={i} />
            {i < steps.length - 1 && (
              <div
                className={cn(
                  'w-0.5 flex-1 my-2',
                  step.status === 'completed' ? 'bg-green-500' : 'bg-gray-200',
                )}
              />
            )}
          </div>
          <div className={cn('w-full pt-1', i < steps.length - 1 && 'pb-8')}>
            <div className="flex flex-row justify-between">
              <Text
                variant={ETextStyleVariant.SmallBold}
                className={cn({
                  'text-gray-500 font-bold': step.status === 'in_progress',
                  'text-gray-300': step.status === 'pending',
                  'text-gray-700': step.status !== 'in_progress' && step.status !== 'pending',
                })}>
                {step.label}{' '}
                {step.status === 'in_progress' && (
                  <span className="inline-flex items-center px-3 pl-3 text-xs font-medium text-white rounded-full bg-canvas-300 gap-1.5">
                    YOU&apos;RE HERE
                  </span>
                )}
              </Text>
              <Text
                variant={ETextStyleVariant.SmallRegular}
                className={step.status === 'pending' ? 'text-gray-300' : 'text-gray-800'}>
                {step.date}
              </Text>
            </div>
            <div>
              {step.description && (
                <Text variant={ETextStyleVariant.SmallRegular} className="text-gray-400">
                  {step.descriptionIsHtml ? (
                    <span
                      dangerouslySetInnerHTML={{
                        __html: step.description,
                      }}
                    />
                  ) : (
                    step.description
                  )}
                </Text>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function ClaimsStatusTracker({
  claim_number,
  status,
  coverage_type,
  vehicle_description,
  incident_date,
  incident_location,
  filed_date,
  closed_at,
  estimated_resolution,
  adjuster_name,
  policy_number,
  current_step,
  total_steps,
  current_step_label,
  last_updated,
  timeline_steps,
  next_update_date,
  next_update_description,
  document_needed,
  claim_team_email,
  onNotifyEmail,
  onNotifyText,
  onBack,
  isLoading,
  reload,
}: ClaimsStatusTrackerProps) {
  const [notifyMethod, setNotifyMethod] = useState<'email' | 'text'>('email');
  const [notifyValue, setNotifyValue] = useState('');

  useEffect(() => {
    trackEvent({
      event: 'Claim : Status Tracker Page View',
      pageType: 'Claim',
      claim_number,
    });
  }, [claim_number]);

  function notifyMe() {
    // TODO
  }

  const claimTeamEmail = claim_team_email;

  const progressPercent = (current_step / total_steps) * 100;
  const nextUpdateDay = next_update_date ? new Date(next_update_date).getDate() : null;
  const daysUntilUpdate = next_update_date
    ? Math.max(
        0,
        Math.ceil((new Date(next_update_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24)),
      )
    : null;

  return (
    <div className="flex justify-center min-h-screen bg-canvas-100">
      <div className="md:w-7/12">
        <div className="max-w-4xl px-4 py-8 mx-auto">
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={onBack}
              className="flex items-center text-sm text-gray-500 gap-1 hover:text-gray-700">
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>
            <button
              type="button"
              onClick={() => {
                reload ? reload() : window.location.reload();
              }}
              className="flex items-center text-sm text-gray-500 gap-1 hover:text-gray-700">
              {isLoading ? (
                <svg
                  className="w-4 h-4 animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  />
                </svg>
              ) : (
                <>
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4 4v6h6M20 20v-6h-6M20 9A8 8 0 006.34 6.34M4 15a8 8 0 0013.66 2.66"
                    />
                  </svg>
                  Refresh
                </>
              )}
            </button>
          </div>

          <Card className="mb-6">
            <div className="p-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <Heading variant={EHeadingStyleVariant.H5}>Claim {claim_number}</Heading>
                  <Text variant={ETextStyleVariant.SmallRegular} className="mt-1 text-gray-400">
                    {coverage_type} &middot; {vehicle_description}
                  </Text>
                  <Text variant={ETextStyleVariant.SmallRegular} className="text-gray-400">
                    Incident: {format(new Date(incident_date), 'MMM dd, yyyy HH:mm:ss')} &middot;{' '}
                    {incident_location}
                  </Text>
                </div>
                <StatusBadge status={status} label={current_step_label} />
              </div>

              <div className="mt-5">
                <div className="flex items-center justify-between mb-1.5">
                  <Text
                    variant={ETextStyleVariant.LegalBold}
                    className="tracking-wide text-gray-400 uppercase">
                    Claim Filed
                  </Text>
                  <Text
                    variant={ETextStyleVariant.LegalBold}
                    className="tracking-wide text-gray-400 uppercase">
                    Resolved
                  </Text>
                </div>
                <div className="w-full h-2 bg-gray-200 rounded-full">
                  <div
                    className="h-2 rounded-full bg-canvas-300 transition-all"
                    style={{ width: `${String(progressPercent)}%` }}
                  />
                </div>
                {status !== 'closed' && (
                  <Text
                    variant={ETextStyleVariant.LegalRegular}
                    className="p-2 text-center text-gray-400 mt-1.5">
                    Step {current_step} of {total_steps} — {current_step_label}
                  </Text>
                )}
              </div>

              <hr className="pt-5" />

              <div className="pt-4 pb-3 grid grid-cols-2 gap-4 md:grid-cols-4">
                {[
                  { label: 'Filed', value: filed_date },
                  {
                    label: status === 'closed' ? 'Resolved' : 'Est. Resolution',
                    value: status === 'closed' ? closed_at : `~${estimated_resolution}`,
                  },
                  { label: 'Adjuster', value: adjuster_name },
                  { label: 'Policy', value: policy_number },
                ].map(item => (
                  <div key={item.label}>
                    <Text
                      variant={ETextStyleVariant.LegalBold}
                      className="tracking-wide text-gray-700 uppercase">
                      {item.label}
                    </Text>
                    <Text variant={ETextStyleVariant.SmallBold}>{item.value}</Text>
                  </div>
                ))}
              </div>

              <div className="pb-2" />
              <hr />

              <div className="flex items-center pt-4 gap-1.5">
                <span className="w-2 h-2 bg-green-500 rounded-full" />
                <Text variant={ETextStyleVariant.LegalRegular} className="text-gray-400">
                  Last update {last_updated} -- pulled live from your claim file
                </Text>
              </div>
            </div>
          </Card>

          {document_needed && (
            <DocumentsNeeded adjusterName={adjuster_name} claimNumber={claim_number} />
          )}

          {nextUpdateDay != null && (
            <div className="p-4 mb-8 border border-blue-200 rounded-lg bg-blue-50">
              <div className="flex items-start gap-3">
                {/*<CalendarDaysIcon className="w-5 h-5" />*/}
                <GeneralCalendarIcon className="w-5 h-5" />
                <div className="flex-1">
                  <Text variant={ETextStyleVariant.SmallBold}>
                    Your next update is already scheduled
                  </Text>
                  {next_update_description && (
                    <Text variant={ETextStyleVariant.SmallRegular} className="mt-1 text-gray-400">
                      {next_update_description}
                    </Text>
                  )}
                  <div className="flex items-center mt-3 text-gray-500 gap-1.5">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}>
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <Text variant={ETextStyleVariant.LegalRegular} className="text-gray-400">
                      Update expected in {daysUntilUpdate} days &middot; {nextUpdateDay}
                    </Text>
                  </div>
                </div>
              </div>
            </div>
          )}

          <Card className="mb-8">
            <div className="p-4">
              <div className="flex items-start gap-3">
                <svg
                  className="w-5 h-5 text-blue-500 mt-0.5 shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <Text variant={ETextStyleVariant.SmallRegular} className="text-gray-700">
                  <strong>This is a general estimate, not a guarantee.</strong> Your claim&apos;s
                  actual steps and timelines may change based on investigation findings. If new
                  information affects you claim, your adjuster will contact you directly.
                </Text>
              </div>
            </div>
          </Card>

          <Card className="mb-8">
            <div className="p-4 border-b">
              <Text variant={ETextStyleVariant.MediumBold}>Your Claims Journey</Text>
            </div>
            <ClaimsTimeline steps={timeline_steps} />
          </Card>

          <Card className="hidden mb-8">
            <div className="p-6">
              <Heading variant={EHeadingStyleVariant.H5}>
                Get notified the moment anything changes
              </Heading>
              <Text variant={ETextStyleVariant.SmallRegular} className="mt-1">
                Stop checking daily — we&apos;ll reach out instantly when your status moves. No
                spam, just updates.
              </Text>
              <div className="flex mt-4 gap-2">
                <Button
                  variant={
                    notifyMethod === 'email'
                      ? EButtonColorVariant.Primary
                      : EButtonColorVariant.Secondary
                  }
                  size={EButtonSizeVariant.Small}
                  onClick={() => {
                    setNotifyMethod('email');
                    onNotifyEmail?.();
                  }}
                  aria-pressed={notifyMethod === 'email'}
                  label={
                    <span className="flex items-center gap-2">
                      {/*<EnvelopeIcon className="w-4 h-4" /> Email*/}
                      <GeneralMessageMessageCircleIcon className="w-4 h-4" /> Email
                    </span>
                  }
                />
                <Button
                  variant={
                    notifyMethod === 'text'
                      ? EButtonColorVariant.Primary
                      : EButtonColorVariant.Secondary
                  }
                  size={EButtonSizeVariant.Small}
                  onClick={() => {
                    setNotifyMethod('text');
                    onNotifyText?.();
                  }}
                  aria-pressed={notifyMethod === 'text'}
                  label={
                    <span className="flex items-center gap-2">
                      <GeneralPhonePhoneCallIcon className="w-4 h-4" /> Text (SMS)
                    </span>
                  }
                />
              </div>
              <div className="flex items-center mt-4 gap-2">
                <TextInput
                  controlled
                  className="w-full"
                  name="notify"
                  label={notifyMethod === 'email' ? 'Email' : 'Phone'}
                  inputValue={notifyValue}
                  onChange={setNotifyValue}
                />
                <Button
                  variant={EButtonColorVariant.Secondary}
                  size={EButtonSizeVariant.Medium}
                  onClick={notifyMe}
                  className="whitespace-nowrap"
                  label="Notify me"
                />
              </div>
            </div>
          </Card>

          <Card className="mb-8">
            <div className="flex flex-col p-4 gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center flex-1 min-w-0 gap-4">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-canvas-200 shrink-0">
                  <GeneralUserUsersIcon className="w-5 h-5 text-gray-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <Text variant={ETextStyleVariant.SmallBold}>Outdoorsy Claims Team</Text>
                  <Text variant={ETextStyleVariant.LegalRegular} className="text-gray-400">
                    Available Mon&ndash;Fri, 9 AM&ndash;4:30 PM CT
                  </Text>
                  <button
                    type="button"
                    onClick={() => {
                      void navigator.clipboard.writeText(claimTeamEmail);
                    }}
                    className="inline-flex items-center max-w-full px-2 py-1 mt-2 rounded bg-canvas-100 gap-2 hover:bg-canvas-200">
                    <Text
                      variant={ETextStyleVariant.LegalRegular}
                      className="text-gray-700 truncate">
                      {claimTeamEmail}
                    </Text>
                    <GeneralCopyIcon className="w-4 h-4 text-gray-500 shrink-0" />
                  </button>
                </div>
              </div>
              <Button
                variant={EButtonColorVariant.Secondary}
                size={EButtonSizeVariant.Medium}
                onClick={() => {
                  window.location.href = `mailto:${claimTeamEmail}`;
                }}
                className="w-full sm:w-auto sm:shrink-0"
                label={
                  <span className="flex items-center gap-2">
                    <GeneralMailIcon className="w-4 h-4" /> Email the team
                  </span>
                }
              />
            </div>
          </Card>

          <Card className="mb-8">
            <div className="p-4 border-b">
              <Text variant={ETextStyleVariant.MediumBold}>Quick Links</Text>
            </div>
            <div className="pl-4 pr-4">
              {faqs.map((faq, i) => (
                <div key={i} className="border-b border-gray-300 last:border-b-0">
                  <div
                    className="flex items-center justify-between w-full py-4 font-medium text-left text-gray-800"
                    aria-expanded="false">
                    <span className="inline-flex items-center gap-2">
                      <a href={faq.link} target="_blank" rel="noopener noreferrer">
                        {faq.title}
                      </a>
                      <GeneralLinkIcon fontSize={12} />
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="mb-8">
            <div className="p-6">
              <Text variant={ETextStyleVariant.SmallRegular} className="mb-3 text-gray-400">
                Still have a question this page doesn&apos;t answer? Call{' '}
                <a href="tel:+18554412006">(855) 441-2006</a> at{' '}
                <a href="mailto:claims@outdoorsy.com">claims@outdoorsy.com</a>.
              </Text>
              <Text
                variant={ETextStyleVariant.LegalRegular}
                className="mt-4 text-center text-gray-400">
                <Text
                  component="span"
                  variant={ETextStyleVariant.LegalBold}
                  className="text-gray-400">
                  First-party claims only.
                </Text>{' '}
                Status and timelines are general guidance, subject to change based on investigation
                findings.
              </Text>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
